export interface SoapResponseTable {
  title: string;
  columns: string[];
  rows: Record<string, string>[];
}

export const LOGISTIKPORTAL_TOUR_COLUMNS = [
  'Filiale',
  'Termin',
  'Kaufvertragsnummer',
  'Kaufvertragsposition',
  'Basisauftragsnummer',
  'Auftragsdatum',
  'Artikelnummer',
  'Ausfuehrung',
  'Artikelbezeichnung',
  'Warengruppe',
  'Reklamationstext',
  'Anrede',
  'Nachname',
  'Vorname',
  'Strasse',
  'PLZ',
  'Ort',
  'Etage',
  'Email',
  'Mobil',
  'Telefon1',
  'Telefon2',
  'Tageszeit',
  'TourSchluessel',
  'TourBezeichnung',
  'LKW',
  'LKWSchluessel',
  'MonteurNachname',
  'MonteurVorname',
  'MonteurSchluessel',
  'ZeitraumVon',
  'ZeitraumBis',
  'Liefermenge',
  'LieferkostenVK',
  'LieferkostenEK',
  'Montagemenge',
  'Montageeinheiten',
  'MontagekostenVK',
  'MontagekostenEK',
  'Zusatztext',
  'Wareneingangsfiliale',
  'RollkartenPosition',
  'Warenwert',
  'Kontingent',
  'NachlassBezugsposition',
  'Zahlungskennzeichen',
];

/** Name, Adresse, Kaufvertrag und Kontaktdaten in der Positionsdetail-Ansicht ausblenden */
export const EXCLUDED_ORDER_POSITION_COLUMNS = new Set([
  'Anrede',
  'Nachname',
  'Vorname',
  'Strasse',
  'PLZ',
  'Ort',
  'Etage',
  'Email',
  'Mobil',
  'Telefon1',
  'Telefon2',
  'Kaufvertragsnummer',
  'Kaufvertragsposition',
  'Basisauftragsnummer',
  'Auftragsdatum',
]);

export interface OrderPositionColumnLabel {
  short: string;
  full: string;
}

const ORDER_POSITION_COLUMN_LABELS: Record<string, OrderPositionColumnLabel> = {
  Filiale: { short: 'Fil.', full: 'Filiale' },
  Termin: { short: 'Termin', full: 'Termin (Kalendertag)' },
  Artikelnummer: { short: 'Art.-Nr.', full: 'Artikelnummer' },
  Ausfuehrung: { short: 'Ausf.', full: 'Ausführung' },
  Artikelbezeichnung: { short: 'Artikel', full: 'Artikelbezeichnung' },
  Warengruppe: { short: 'WG', full: 'Warengruppe' },
  Reklamationstext: { short: 'Rekla', full: 'Reklamationstext' },
  Tageszeit: { short: 'TZ', full: 'Tageszeit (ganztags / am / pm)' },
  TourSchluessel: { short: 'Tour', full: 'TourSchlüssel (MHS)' },
  TourBezeichnung: { short: 'Tour-Bez.', full: 'TourBezeichnung' },
  LKW: { short: 'LKW', full: 'LKW-Bezeichnung' },
  LKWSchluessel: { short: 'LKW-Key', full: 'LKW-Schlüsselnummer' },
  MonteurNachname: { short: 'Mont-N', full: 'Monteur Nachname' },
  MonteurVorname: { short: 'Mont-V', full: 'Monteur Vorname' },
  MonteurSchluessel: { short: 'Mont-Key', full: 'Monteur-Schlüsselnummer' },
  ZeitraumVon: { short: 'Von', full: 'Zeitraum von (Dezimalstunde, z. B. 8.5 = 08:30)' },
  ZeitraumBis: { short: 'Bis', full: 'Zeitraum bis (Dezimalstunde, z. B. 10.75 = 10:45)' },
  Liefermenge: { short: 'Lief.', full: 'Liefermenge' },
  LieferkostenVK: { short: 'Lief.VK', full: 'Lieferkosten VK' },
  LieferkostenEK: { short: 'Lief.EK', full: 'Lieferkosten EK' },
  Montagemenge: { short: 'Mont.', full: 'Montagemenge' },
  Montageeinheiten: { short: 'ME', full: 'Montageeinheiten' },
  MontagekostenVK: { short: 'Mont.VK', full: 'Montagekosten VK' },
  MontagekostenEK: { short: 'Mont.EK', full: 'Montagekosten EK' },
  Zusatztext: { short: 'Zusatz', full: 'Zusatztext zum Gesamtauftrag' },
  Wareneingangsfiliale: { short: 'WE-Fil.', full: 'Wareneingangsfiliale' },
  RollkartenPosition: { short: 'Rollk.', full: 'RollkartenPosition' },
  Warenwert: { short: 'WW', full: 'Warenwert (Verkaufspreis)' },
  Kontingent: { short: 'Kont.', full: 'Kontingent (Möbel, Küchen, Online)' },
  NachlassBezugsposition: { short: 'Nachl.Pos.', full: 'NachlassBezugsposition' },
  Zahlungskennzeichen: { short: 'ZK', full: 'Zahlungskennzeichen' },
};

export function getOrderPositionColumnLabel(column: string): OrderPositionColumnLabel {
  const mapped = ORDER_POSITION_COLUMN_LABELS[column];
  if (mapped) {
    return mapped;
  }

  return {
    short: column.length > 10 ? `${column.slice(0, 9)}…` : column,
    full: column,
  };
}

const PREFERRED_COLUMNS: Record<string, string[]> = {
  auftraege: LOGISTIKPORTAL_TOUR_COLUMNS,
  Touren: LOGISTIKPORTAL_TOUR_COLUMNS,
};

export function getOrderPositionDisplayColumns(table: SoapResponseTable): string[] {
  const available = new Set(table.columns);
  const preferred = LOGISTIKPORTAL_TOUR_COLUMNS.filter(
    column => available.has(column) && !EXCLUDED_ORDER_POSITION_COLUMNS.has(column)
  );
  const preferredSet = new Set(preferred);
  const rest = table.columns.filter(
    column => !EXCLUDED_ORDER_POSITION_COLUMNS.has(column) && !preferredSet.has(column)
  );

  return preferred.concat(rest);
}

export const ESSENTIAL_TOUR_COLUMNS = [
  'Filiale',
  'Termin',
  'Kaufvertragsnummer',
  'Nachname',
  'Vorname',
  'Ort',
  'PLZ',
  'TourSchluessel',
  'TourBezeichnung',
  'Tageszeit',
  'Artikelbezeichnung',
  'MonteurNachname',
  'LKW',
  'Kontingent',
];

const STICKY_COLUMNS = ['Filiale', 'Termin', 'Kaufvertragsnummer', 'Nachname'];

const STICKY_COLUMN_WIDTHS: Record<string, number> = {
  Filiale: 52,
  Termin: 96,
  Kaufvertragsnummer: 108,
  Nachname: 112,
};

export const ROW_NUMBER_COLUMN_WIDTH = 42;

const NARROW_COLUMNS = new Set([
  'Filiale',
  'PLZ',
  'Etage',
  'LKWSchluessel',
  'MonteurSchluessel',
  'ZeitraumVon',
  'ZeitraumBis',
  'Liefermenge',
  'Montagemenge',
  'Montageeinheiten',
  'RollkartenPosition',
  'Zahlungskennzeichen',
  'Kaufvertragsposition',
  'Ausfuehrung',
  'Warengruppe',
  'Wareneingangsfiliale',
  'Anrede',
  'Tageszeit',
  'Kontingent',
]);

const LONG_TEXT_COLUMNS = new Set([
  'Zusatztext',
  'Reklamationstext',
  'TourBezeichnung',
  'Artikelbezeichnung',
  'Strasse',
  'Email',
]);

export type TableColumnKind = 'sticky' | 'narrow' | 'long' | 'default';

export function getColumnKind(column: string): TableColumnKind {
  if (STICKY_COLUMNS.indexOf(column) !== -1) {
    return 'sticky';
  }

  if (LONG_TEXT_COLUMNS.has(column)) {
    return 'long';
  }

  if (NARROW_COLUMNS.has(column)) {
    return 'narrow';
  }

  return 'default';
}

export function getStickyColumnIndex(column: string, visibleColumns: string[]): number {
  const sticky = visibleColumns.filter((name) => STICKY_COLUMNS.indexOf(name) !== -1);
  return sticky.indexOf(column);
}

export function getStickyLeft(column: string, visibleColumns: string[]): number {
  const sticky = visibleColumns.filter((name) => STICKY_COLUMNS.indexOf(name) !== -1);
  const index = sticky.indexOf(column);
  if (index === -1) {
    return 0;
  }

  let left = ROW_NUMBER_COLUMN_WIDTH;
  for (let i = 0; i < index; i++) {
    left += getStickyWidth(sticky[i]);
  }

  return left;
}

export function getStickyWidth(column: string): number {
  return STICKY_COLUMN_WIDTHS[column] || 90;
}

export function filterTableColumns(table: SoapResponseTable, mode: 'essential' | 'all'): string[] {
  if (mode === 'all' || table.title !== 'auftraege') {
    return table.columns;
  }

  const essential = ESSENTIAL_TOUR_COLUMNS.filter((column) => table.columns.indexOf(column) !== -1);
  return essential.length ? essential : table.columns;
}

export interface TableColumnMeta {
  kind: TableColumnKind;
  sticky: boolean;
  left: number;
  width: number;
}

export interface TableViewState {
  table: SoapResponseTable;
  displayColumns: string[];
  columnMeta: Record<string, TableColumnMeta>;
}

export function buildTableViewState(table: SoapResponseTable, mode: 'essential' | 'all'): TableViewState {
  const displayColumns = filterTableColumns(table, mode);
  const columnMeta: Record<string, TableColumnMeta> = {};

  for (const column of displayColumns) {
    columnMeta[column] = {
      kind: getColumnKind(column),
      sticky: getColumnKind(column) === 'sticky' && getStickyColumnIndex(column, displayColumns) !== -1,
      left: getStickyLeft(column, displayColumns),
      width: getStickyWidth(column),
    };
  }

  return {
    table,
    displayColumns,
    columnMeta,
  };
}

function cellValue(value: unknown): string {
  if (value == null) {
    return '';
  }

  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  return String(value);
}

function isMetaKey(key: string): boolean {
  return key === '@attributes' || key.startsWith('@');
}

function isNumericKeyObject(obj: Record<string, unknown>): boolean {
  const keys = Object.keys(obj).filter((key) => !isMetaKey(key));
  return keys.length > 0 && keys.every((key) => /^\d+$/.test(key));
}

function flattenRecord(record: Record<string, unknown>, prefix = ''): Record<string, string> {
  const result: Record<string, string> = {};

  for (const [key, value] of Object.entries(record)) {
    if (isMetaKey(key)) {
      continue;
    }

    const column = prefix ? `${prefix}.${key}` : key;

    if (value != null && typeof value === 'object' && !Array.isArray(value)) {
      Object.assign(result, flattenRecord(value as Record<string, unknown>, column));
    } else {
      result[column] = cellValue(value);
    }
  }

  return result;
}

function extractRowArray(value: unknown): Record<string, unknown>[] {
  if (value == null) {
    return [];
  }

  if (Array.isArray(value)) {
    return value.filter((entry) => entry != null && typeof entry === 'object') as Record<string, unknown>[];
  }

  if (typeof value !== 'object') {
    return [];
  }

  const obj = value as Record<string, unknown>;

  if ('item' in obj) {
    return extractRowArray(obj.item);
  }

  if (isNumericKeyObject(obj)) {
    return Object.keys(obj)
      .filter((key) => /^\d+$/.test(key))
      .sort((left, right) => Number(left) - Number(right))
      .map((key) => obj[key] as Record<string, unknown>)
      .filter((entry) => entry != null && typeof entry === 'object');
  }

  const values = Object.entries(obj)
    .filter(([key]) => !isMetaKey(key))
    .map(([, entry]) => entry);
  const hasPrimitive = values.some((entry) => entry == null || typeof entry !== 'object');
  const hasNestedObject = values.some(
    (entry) => entry != null && typeof entry === 'object' && !Array.isArray(entry)
  );

  if (hasPrimitive && !hasNestedObject) {
    return [obj];
  }

  return [];
}

function orderColumns(columns: string[], title: string): string[] {
  const preferred = PREFERRED_COLUMNS[title] || [];
  const preferredSet = new Set(preferred);
  const ordered = preferred.filter((column) => columns.indexOf(column) !== -1);
  const rest = columns.filter((column) => !preferredSet.has(column));
  return ordered.concat(rest);
}

function collectColumns(flatRows: Record<string, string>[], title: string): string[] {
  const seen = new Set<string>();
  const columns: string[] = [];

  for (const row of flatRows) {
    for (const key of Object.keys(row)) {
      if (!seen.has(key)) {
        seen.add(key);
        columns.push(key);
      }
    }
  }

  return orderColumns(columns, title);
}

function arrayToTable(title: string, items: Record<string, unknown>[]): SoapResponseTable {
  const flatRows = items.map((item) => flattenRecord(item));
  const columns = collectColumns(flatRows, title);

  return {
    title,
    columns,
    rows: flatRows.map((row) => {
      const normalized: Record<string, string> = {};
      for (const column of columns) {
        normalized[column] = row[column] ?? '';
      }
      return normalized;
    }),
  };
}

function rowsToTable(title: string, rows: Record<string, string>[]): SoapResponseTable {
  const columns = collectColumns(rows, title);
  return {
    title,
    columns,
    rows: rows.map((row) => {
      const normalized: Record<string, string> = {};
      for (const column of columns) {
        normalized[column] = row[column] ?? '';
      }
      return normalized;
    }),
  };
}

function extractScalarRows(obj: Record<string, unknown>): Record<string, string>[] {
  const rows: Record<string, string>[] = [];

  for (const [key, value] of Object.entries(obj)) {
    if (isMetaKey(key)) {
      continue;
    }

    if (value == null || typeof value !== 'object') {
      rows.push({ Feld: key, Wert: cellValue(value) });
      continue;
    }

    if (!extractRowArray(value).length) {
      rows.push({ Feld: key, Wert: cellValue(value) });
    }
  }

  return rows;
}

function localName(element: Element): string {
  return element.localName || element.tagName.replace(/^[^:]+:/, '');
}

function elementToRow(element: Element): Record<string, string> {
  const row: Record<string, string> = {};

  for (let index = 0; index < element.children.length; index++) {
    const child = element.children[index];
    const name = localName(child);
    if (!name || name === 'item') {
      continue;
    }

    row[name] = (child.textContent || '').trim();
  }

  return row;
}

function parseSoapXmlTables(rawXml: string): SoapResponseTable[] {
  const xml = rawXml.trim();
  if (!xml) {
    return [];
  }

  try {
    const doc = new DOMParser().parseFromString(xml, 'text/xml');
    if (doc.getElementsByTagName('parsererror').length) {
      return [];
    }

    const tables: SoapResponseTable[] = [];
    const containers = Array.from(doc.getElementsByTagName('*')).filter((node) => localName(node) === 'auftraege');

    for (let index = 0; index < containers.length; index++) {
      const container = containers[index];
      const items = Array.from(container.children).filter((child) => localName(child) === 'item');
      const rows = items.map((item) => elementToRow(item)).filter((row) => Object.keys(row).length > 0);
      if (rows.length) {
        tables.push(rowsToTable('auftraege', rows));
      }
    }

    return tables;
  } catch {
    return [];
  }
}

export function buildResponseTables(data: unknown): SoapResponseTable[] {
  if (data == null) {
    return [];
  }

  if (Array.isArray(data)) {
    const items = extractRowArray(data);
    return items.length ? [arrayToTable('Daten', items)] : [];
  }

  if (typeof data !== 'object') {
    return [{
      title: 'Wert',
      columns: ['Wert'],
      rows: [{ Wert: cellValue(data) }],
    }];
  }

  const obj = data as Record<string, unknown>;
  const tables: SoapResponseTable[] = [];

  for (const [key, value] of Object.entries(obj)) {
    if (isMetaKey(key)) {
      continue;
    }

    const items = extractRowArray(value);
    if (items.length) {
      tables.push(arrayToTable(key, items));
    }
  }

  const scalarRows = extractScalarRows(obj);
  if (scalarRows.length) {
    tables.unshift({
      title: 'Zusammenfassung',
      columns: ['Feld', 'Wert'],
      rows: scalarRows,
    });
  }

  if (!tables.length) {
    const flat = flattenRecord(obj);
    const columns = Object.keys(flat);
    if (columns.length) {
      tables.push({
        title: 'Daten',
        columns,
        rows: [flat],
      });
    }
  }

  return tables;
}

export function buildResponseTablesFromSoapResult(parsed: unknown, rawXml?: string | null): SoapResponseTable[] {
  const parsedTables = buildResponseTables(parsed);
  const auftraege = parsedTables.find((table) => table.title === 'auftraege');
  const hasTourKeys = !!auftraege?.rows?.some((row) => {
    const tour =
      row.TourSchluessel ||
      row.tourSchluessel ||
      row.TourBezeichnung ||
      row.LKW ||
      row.LKWSchluessel ||
      '';
    return String(tour).trim().length > 0;
  });

  // Wie in roller-api: wenn Aufträge ohne Tour-Felder ankommen, Raw-XML bevorzugen
  if (auftraege?.rows?.length && !hasTourKeys && rawXml) {
    const xmlTables = parseSoapXmlTables(rawXml);
    if (xmlTables.some((t) => t.title === 'auftraege' && t.rows.length)) {
      return xmlTables;
    }
  }

  const dataTables = parsedTables.filter((table) => table.title !== 'Zusammenfassung' && table.rows.length > 0);
  if (dataTables.length) {
    return parsedTables;
  }

  const xmlTables = parseSoapXmlTables(rawXml || '');
  if (xmlTables.length) {
    return xmlTables;
  }

  return parsedTables;
}

export function tablesToTsv(tables: SoapResponseTable[]): string {
  return tables.map((table) => {
    const header = table.columns.join('\t');
    const body = table.rows.map((row) => table.columns.map((col) => row[col] ?? '').join('\t')).join('\n');
    return `${table.title}\n${header}\n${body}`;
  }).join('\n\n');
}
